import { useEffect, useMemo, useCallback } from "preact/hooks";
import type { State, Payor, Specialties } from "@services/filter-api/types";

interface FilterStateSyncProps {
  selectedItems: {
    state: State | null;
    insurance: Payor | null;
    specialty: Specialties | null;
  };
  syncedSelectedItems: {
    state: State | null;
    insurance: Payor | null;
    specialty: Specialties | null;
  };
  syncedFields: {
    state: boolean;
    insurance: boolean;
    specialty: boolean;
    errors: boolean;
  };
  errors?: {
    state: string;
    insurance: string;
  };
  syncedErrors: {
    state: string;
    insurance: string;
  };
  handleGetSelectedState: (item: any) => void;
  handleGetSelectedInsurance: (item: any) => void;
  handleGetSelectedNeeds: (items: any[]) => void;
  syncedHandleGetSelectedState: (item: any) => void;
  syncedHandleGetSelectedInsurance: (item: any) => void;
  syncedHandleGetSelectedNeeds: (items: any[]) => void;
  syncedHandleValidationUpdate: (errors: { state: string; insurance: string }) => void;
}

export const useFilterStateSync = ({
  selectedItems,
  syncedSelectedItems,
  syncedFields,
  errors,
  syncedErrors,
  handleGetSelectedState,
  handleGetSelectedInsurance,
  handleGetSelectedNeeds,
  syncedHandleGetSelectedState,
  syncedHandleGetSelectedInsurance,
  syncedHandleGetSelectedNeeds,
  syncedHandleValidationUpdate,
}: FilterStateSyncProps) => {
  const combinedSelectedItems = useMemo(() => ({
    state: syncedFields.state ? syncedSelectedItems.state : selectedItems.state,
    insurance: syncedFields.insurance ? syncedSelectedItems.insurance : selectedItems.insurance,
    specialty: syncedFields.specialty ? syncedSelectedItems.specialty : selectedItems.specialty,
  }), [
    syncedFields.state,
    syncedFields.insurance,
    syncedFields.specialty,
    syncedSelectedItems.state,
    syncedSelectedItems.insurance,
    syncedSelectedItems.specialty,
    selectedItems.state,
    selectedItems.insurance,
    selectedItems.specialty,
  ]);

  const combinedErrors = useMemo(() => ({
    state: syncedFields.errors ? syncedErrors.state : (errors?.state || ""),
    insurance: syncedFields.errors ? syncedErrors.insurance : (errors?.insurance || ""),
  }), [syncedFields.errors, syncedErrors.state, syncedErrors.insurance, errors?.state, errors?.insurance]);

  const combinedHandleGetSelectedState = useCallback((item: any) => {
    handleGetSelectedState(item);
    syncedHandleGetSelectedState(item);
  }, [handleGetSelectedState, syncedHandleGetSelectedState]);

  const combinedHandleGetSelectedInsurance = useCallback((item: any) => {
    handleGetSelectedInsurance(item);
    syncedHandleGetSelectedInsurance(item);
  }, [handleGetSelectedInsurance, syncedHandleGetSelectedInsurance]);

  const combinedHandleGetSelectedNeeds = useCallback((items: any[]) => {
    handleGetSelectedNeeds(items);
    syncedHandleGetSelectedNeeds(items);
  }, [handleGetSelectedNeeds, syncedHandleGetSelectedNeeds]);

  // Only emit validation updates if there are actual errors and they're different from synced errors
  useEffect(() => {
    if (errors && (errors.state !== syncedErrors.state || errors.insurance !== syncedErrors.insurance)) {
      if (typeof syncedHandleValidationUpdate === 'function') {
        syncedHandleValidationUpdate(errors);
      }
    }
  }, [errors, syncedErrors, syncedHandleValidationUpdate]);

  return {
    combinedSelectedItems,
    combinedErrors,
    combinedHandleGetSelectedState,
    combinedHandleGetSelectedInsurance,
    combinedHandleGetSelectedNeeds,
  };
};
